import { Box, type SxProps, type Theme } from "@mui/material";
import { Icon } from "@iconify/react";
import type { CSSProperties } from "react";

/**
 * Единый формат значения поля `icon` (Currency.icon / Network.icon):
 *
 *   • "iconify:solar:bitcoin-bold-duotone"  — рендерится через @iconify/react;
 *   • "https://…/logo.png"                   — обычная картинка;
 *   • "data:image/png;base64,…"              — inline base64 (загружено админом).
 *
 * WebApp определяет тип по префиксу и выбирает <Icon icon={...} /> vs <img src={...} />.
 * Использовать в любом месте, где раньше стоял `<img src={currency.icon}>` /
 * `<img src={network.logoUrl}>`.
 */
export const ICONIFY_PREFIX = "iconify:";

/**
 * True для строк вида `iconify:<name>`. НЕ используем type guard `v is string`
 * — он бы неправильно сужал negative-ветку (мешал бы дальше проверить URL).
 * Возвращаем обычный boolean.
 */
export function isIconifyValue(v: string | null | undefined): boolean {
  return typeof v === "string" && v.startsWith(ICONIFY_PREFIX);
}

/** Возвращает имя Iconify без префикса. Пустая строка — если это не iconify. */
export function iconifyName(v: string | null | undefined): string {
  if (!isIconifyValue(v)) return "";
  return (v as string).slice(ICONIFY_PREFIX.length);
}

interface CurrencyIconProps {
  /** Значение поля `icon` из БД. Может быть null. */
  value: string | null | undefined;
  /** Размер квадрата (px). */
  size?: number;
  /**
   * Альтернативная иконка для рендера, если `value` пустая. Обычно
   * передаём код валюты/сети — покажется первая буква на цветном круге.
   */
  fallbackLetter?: string;
  /** Форма контейнера (по умолчанию — круг). */
  shape?: "circle" | "square" | "rounded";
  /** Дополнительный style/sx. */
  sx?: SxProps<Theme>;
  /** alt-текст для img. */
  alt?: string;
  /** Опциональный style — для `img` (например object-fit). */
  imgStyle?: CSSProperties;
}

/**
 * Универсальная иконка валюты/сети. Заменяет разбросанные по коду
 * `<Box component="img" src={icon}>` и mini-компоненты `AssetIcon`.
 */
export function CurrencyIcon({
  value,
  size = 32,
  fallbackLetter,
  shape = "circle",
  sx,
  alt,
  imgStyle,
}: CurrencyIconProps) {
  const borderRadius =
    shape === "circle" ? "50%" : shape === "rounded" ? "25%" : "6px";

  const baseSx: SxProps<Theme> = {
    width: size,
    height: size,
    borderRadius,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    flexShrink: 0,
    ...(sx as object),
  };

  // 1) Iconify
  if (isIconifyValue(value)) {
    return (
      <Box sx={baseSx}>
        <Icon icon={iconifyName(value)} width={size} height={size} />
      </Box>
    );
  }

  // 2) URL / data-URL
  if (typeof value === "string" && value.trim().length > 0) {
    return (
      <Box
        component="img"
        src={value}
        alt={alt ?? fallbackLetter ?? ""}
        sx={{ ...baseSx, objectFit: "cover" }}
        style={imgStyle}
        onError={(e) => {
          // Скрываем битую картинку — родитель может нарисовать fallback,
          // если сам его знает; иначе останется пустой кружок нужного размера.
          (e.currentTarget as HTMLImageElement).style.visibility = "hidden";
        }}
      />
    );
  }

  // 3) Fallback — первая буква на цветном фоне
  const letter = (fallbackLetter ?? "?").trim().slice(0, 1).toUpperCase();
  return (
    <Box
      sx={{
        ...baseSx,
        bgcolor: "action.selected",
        color: "text.primary",
        fontWeight: 700,
        fontSize: Math.round(size * 0.42),
        lineHeight: 1,
      }}
    >
      {letter || "?"}
    </Box>
  );
}
